import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { LatencyHistoryRow } from '../lib/api.js';

interface Props {
  rows: LatencyHistoryRow[];
  targets: string[];
  height?: number;
  title: string;
  subtitle?: string;
}

interface RegionComparisonProps {
  rows: LatencyHistoryRow[];
  regions: string[];
  height?: number;
  title: string;
  subtitle?: string;
}

interface ChartPoint {
  ts: string;
  p50_ms: number;
  p95_ms: number;
  sampleCount: number;
}

interface RegionPoint {
  ts: string;
  region: string;
  p95_ms: number;
  sampleCount: number;
}

const WIDTH = 960;
const PAD_LEFT = 64;
const PAD_RIGHT = 24;
const PAD_TOP = 22;
const PAD_BOTTOM = 40;
const GRID_LINES = 4;

const REGION_COLORS: Record<string, string> = {
  ENAM: '#7cc6ff',
  WNAM: '#4ade80',
  WEUR: '#facc15',
  EEUR: '#fb923c',
  SAM: '#ff2a92',
  OC: '#00fff7',
  APAC: '#ff7a1a',
  AFR: '#f87171',
  ME: '#2dd4bf',
};

function formatMs(value: number): string {
  if (!Number.isFinite(value)) return '0ms';
  return `${Math.round(value)}ms`;
}

function formatTimeLabel(ts: string): string {
  const parsed = Date.parse(ts);
  if (!Number.isFinite(parsed)) return ts;
  return new Date(parsed).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function averageRows(rows: LatencyHistoryRow[], metric: 'p50_ms' | 'p95_ms'): number {
  if (rows.length === 0) return 0;
  const total = rows.reduce((sum, row) => sum + row[metric], 0);
  return total / rows.length;
}

function buildYScale(values: number[]): { min: number; max: number; ticks: number[] } {
  const finite = values.filter(Number.isFinite);
  if (finite.length === 0) {
    return { min: 0, max: 100, ticks: [0, 25, 50, 75, 100] };
  }

  const rawMin = Math.min(...finite);
  const rawMax = Math.max(...finite);
  const span = Math.max(10, rawMax - rawMin);
  const min = Math.max(0, Math.floor((rawMin - span * 0.12) / 10) * 10);
  const max = Math.ceil((rawMax + span * 0.18) / 10) * 10;
  const step = Math.max(1, (max - min) / GRID_LINES);
  return {
    min,
    max,
    ticks: Array.from({ length: GRID_LINES + 1 }, (_, index) => min + step * index),
  };
}

function pointPath(points: Array<[number, number]>): string | null {
  if (points.length < 2) return null;
  return points.map(([x, y], index) => `${index === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ');
}

function selectedTimeLabels(points: Array<{ ts: string }>): Array<{ ts: string; index: number }> {
  if (points.length <= 3) return points.map((point, index) => ({ ts: point.ts, index }));
  return [
    { ts: points[0]?.ts ?? '', index: 0 },
    { ts: points[Math.floor((points.length - 1) / 2)]?.ts ?? '', index: Math.floor((points.length - 1) / 2) },
    { ts: points[points.length - 1]?.ts ?? '', index: points.length - 1 },
  ].filter((entry) => entry.ts.length > 0);
}

function ChartFrame({
  title,
  subtitle,
  children,
  legend,
  empty,
}: {
  title: string;
  subtitle?: string;
  children?: ReactNode;
  legend?: ReactNode;
  empty?: string;
}) {
  return (
    <div className="border border-border rounded p-3 bg-surface/30">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        <div>
          <div className="text-[10px] font-mono text-white uppercase tracking-[2px]">
            {title}
          </div>
          {subtitle ? <div className="text-[11px] text-muted mt-1">{subtitle}</div> : null}
        </div>
        {legend ? <div className="flex flex-wrap justify-end gap-x-3 gap-y-1 text-[11px] font-mono">{legend}</div> : null}
      </div>
      {empty ? (
        <div className="border border-border rounded px-3 py-6 text-center text-xs text-dim">
          {empty}
        </div>
      ) : (
        children
      )}
    </div>
  );
}

export default function LatencyTrendChart({
  rows,
  targets,
  title,
  subtitle,
  height = 230,
}: Props) {
  const series = useMemo(() => {
    const grouped = new Map<string, LatencyHistoryRow[]>();
    for (const row of rows) {
      if (!targets.includes(row.target)) continue;
      const bucket = grouped.get(row.ts);
      if (bucket) bucket.push(row);
      else grouped.set(row.ts, [row]);
    }

    return Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([ts, rowSet]) => ({
        ts,
        p50_ms: averageRows(rowSet, 'p50_ms'),
        p95_ms: averageRows(rowSet, 'p95_ms'),
        sampleCount: rowSet.reduce((sum, row) => sum + row.samples, 0),
      } satisfies ChartPoint));
  }, [rows, targets]);

  const allValues = series.flatMap((point) => [point.p50_ms, point.p95_ms]);
  const yScale = buildYScale(allValues);
  const innerW = WIDTH - PAD_LEFT - PAD_RIGHT;
  const innerH = height - PAD_TOP - PAD_BOTTOM;
  const xFor = (index: number): number =>
    PAD_LEFT + (series.length <= 1 ? 0.5 : index / (series.length - 1)) * innerW;
  const yFor = (value: number): number => {
    const ratio = (value - yScale.min) / Math.max(1, yScale.max - yScale.min);
    return PAD_TOP + (1 - Math.max(0, Math.min(1, ratio))) * innerH;
  };
  const p50Points = series.map((point, index) => [xFor(index), yFor(point.p50_ms)] as [number, number]);
  const p95Points = series.map((point, index) => [xFor(index), yFor(point.p95_ms)] as [number, number]);
  const timeLabels = selectedTimeLabels(series);

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      empty={series.length === 0 ? `not enough data for ${title}` : undefined}
      legend={
        <>
          <span><span style={{ color: '#7cc6ff' }}>p50</span> median</span>
          <span><span style={{ color: '#facc15' }}>p95</span> tail latency</span>
          <span className="text-dim">{series.length} snapshots</span>
        </>
      }
    >
      <svg width="100%" viewBox={`0 0 ${WIDTH} ${height}`} role="img" aria-label={`Latency trend ${title}`}>
        <rect x="0" y="0" width={WIDTH} height={height} fill="transparent" />
        <g stroke="#2a3142" strokeWidth="1">
          {yScale.ticks.map((tick) => {
            const y = yFor(tick);
            return (
              <g key={tick}>
                <line x1={PAD_LEFT} y1={y} x2={WIDTH - PAD_RIGHT} y2={y} opacity="0.75" />
                <text x={PAD_LEFT - 10} y={y + 4} textAnchor="end" fill="#9aa3b8" fontSize="11">
                  {formatMs(tick)}
                </text>
              </g>
            );
          })}
          <line x1={PAD_LEFT} y1={PAD_TOP} x2={PAD_LEFT} y2={height - PAD_BOTTOM} />
          <line x1={PAD_LEFT} y1={height - PAD_BOTTOM} x2={WIDTH - PAD_RIGHT} y2={height - PAD_BOTTOM} />
        </g>
        <text x="16" y={PAD_TOP + innerH / 2} fill="#5d6477" fontSize="11" transform={`rotate(-90 16 ${PAD_TOP + innerH / 2})`}>
          latency (ms)
        </text>
        <text x={PAD_LEFT + innerW / 2} y={height - 6} textAnchor="middle" fill="#5d6477" fontSize="11">
          time
        </text>
        <g fill="#9aa3b8" fontSize="11">
          {timeLabels.map(({ ts, index }) => (
            <text key={`${ts}-${index}`} x={xFor(index)} y={height - PAD_BOTTOM + 18} textAnchor="middle">
              {formatTimeLabel(ts)}
            </text>
          ))}
        </g>
        <g fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          {pointPath(p50Points) ? <path d={pointPath(p50Points) as string} stroke="#7cc6ff" /> : null}
          {pointPath(p95Points) ? <path d={pointPath(p95Points) as string} stroke="#facc15" /> : null}
        </g>
        <g>
          {series.map((point, index) => (
            <circle key={`p95-${point.ts}`} cx={xFor(index)} cy={yFor(point.p95_ms)} r="3" fill="#facc15" />
          ))}
          {series.map((point, index) => (
            <circle key={`p50-${point.ts}`} cx={xFor(index)} cy={yFor(point.p50_ms)} r="3" fill="#7cc6ff" />
          ))}
        </g>
      </svg>
    </ChartFrame>
  );
}

export function RegionLatencyComparisonChart({
  rows,
  regions,
  title,
  subtitle,
  height = 260,
}: RegionComparisonProps) {
  const [activeRegion, setActiveRegion] = useState<string | null>(null);
  const { timestamps, seriesByRegion } = useMemo(() => {
    const grouped = new Map<string, Map<string, LatencyHistoryRow[]>>();
    for (const row of rows) {
      if (!regions.includes(row.region)) continue;
      const regionRows = grouped.get(row.region) ?? new Map<string, LatencyHistoryRow[]>();
      const bucket = regionRows.get(row.ts);
      if (bucket) bucket.push(row);
      else regionRows.set(row.ts, [row]);
      grouped.set(row.region, regionRows);
    }

    const allTimestamps = Array.from(new Set(rows.map((row) => row.ts))).sort();
    const nextSeries = new Map<string, RegionPoint[]>();
    for (const region of regions) {
      const regionRows = grouped.get(region);
      if (!regionRows) continue;
      const points = Array.from(regionRows.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([ts, rowSet]) => ({
          ts,
          region,
          p95_ms: averageRows(rowSet, 'p95_ms'),
          sampleCount: rowSet.reduce((sum, row) => sum + row.samples, 0),
        } satisfies RegionPoint));
      if (points.length > 0) nextSeries.set(region, points);
    }
    return { timestamps: allTimestamps, seriesByRegion: nextSeries };
  }, [rows, regions]);

  const allValues = Array.from(seriesByRegion.values()).flatMap((points) => points.map((point) => point.p95_ms));
  const yScale = buildYScale(allValues);
  const innerW = WIDTH - PAD_LEFT - PAD_RIGHT;
  const innerH = height - PAD_TOP - PAD_BOTTOM;
  const timestampIndex = new Map(timestamps.map((ts, index) => [ts, index]));
  const xForTimestamp = (ts: string): number => {
    const index = timestampIndex.get(ts) ?? 0;
    return PAD_LEFT + (timestamps.length <= 1 ? 0.5 : index / (timestamps.length - 1)) * innerW;
  };
  const yFor = (value: number): number => {
    const ratio = (value - yScale.min) / Math.max(1, yScale.max - yScale.min);
    return PAD_TOP + (1 - Math.max(0, Math.min(1, ratio))) * innerH;
  };
  const timeLabels = selectedTimeLabels(timestamps.map((ts) => ({ ts })));
  const activeColor = activeRegion ? REGION_COLORS[activeRegion] ?? '#9aa3b8' : '#9aa3b8';
  const activePoints = activeRegion ? seriesByRegion.get(activeRegion) ?? [] : [];
  const activeAverage =
    activePoints.length === 0
      ? null
      : activePoints.reduce((sum, point) => sum + point.p95_ms, 0) / activePoints.length;

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      empty={seriesByRegion.size === 0 ? `not enough data for ${title}` : undefined}
      legend={regions.map((region) => (
        <span key={region} className="inline-flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: REGION_COLORS[region] ?? '#9aa3b8' }} />
          <span
            className={activeRegion && activeRegion !== region ? 'opacity-45' : undefined}
            style={{ color: REGION_COLORS[region] ?? '#9aa3b8' }}
          >
            {region}
          </span>
        </span>
      ))}
    >
      <svg width="100%" viewBox={`0 0 ${WIDTH} ${height}`} role="img" aria-label={`Regional latency comparison ${title}`}>
        <rect x="0" y="0" width={WIDTH} height={height} fill="transparent" />
        <text x={PAD_LEFT} y="14" fill={activeColor} fontSize="12" fontWeight="700">
          {activeRegion
            ? `${activeRegion} p95 trend${activeAverage === null ? '' : ` | avg ${formatMs(activeAverage)}`}`
            : 'Hover a region line to identify it'}
        </text>
        <g stroke="#2a3142" strokeWidth="1">
          {yScale.ticks.map((tick) => {
            const y = yFor(tick);
            return (
              <g key={tick}>
                <line x1={PAD_LEFT} y1={y} x2={WIDTH - PAD_RIGHT} y2={y} opacity="0.75" />
                <text x={PAD_LEFT - 10} y={y + 4} textAnchor="end" fill="#9aa3b8" fontSize="11">
                  {formatMs(tick)}
                </text>
              </g>
            );
          })}
          <line x1={PAD_LEFT} y1={PAD_TOP} x2={PAD_LEFT} y2={height - PAD_BOTTOM} />
          <line x1={PAD_LEFT} y1={height - PAD_BOTTOM} x2={WIDTH - PAD_RIGHT} y2={height - PAD_BOTTOM} />
        </g>
        <text x="16" y={PAD_TOP + innerH / 2} fill="#5d6477" fontSize="11" transform={`rotate(-90 16 ${PAD_TOP + innerH / 2})`}>
          latency p95 (ms)
        </text>
        <text x={PAD_LEFT + innerW / 2} y={height - 6} textAnchor="middle" fill="#5d6477" fontSize="11">
          time
        </text>
        <g fill="#9aa3b8" fontSize="11">
          {timeLabels.map(({ ts }) => (
            <text key={ts} x={xForTimestamp(ts)} y={height - PAD_BOTTOM + 18} textAnchor="middle">
              {formatTimeLabel(ts)}
            </text>
          ))}
        </g>
        <g fill="none" strokeLinecap="round" strokeLinejoin="round">
          {Array.from(seriesByRegion.entries()).map(([region, points]) => {
            const path = pointPath(points.map((point) => [xForTimestamp(point.ts), yFor(point.p95_ms)] as [number, number]));
            const isActive = activeRegion === null || activeRegion === region;
            return path ? (
              <g key={region}>
                <path
                  d={path}
                  stroke="transparent"
                  strokeWidth="14"
                  pointerEvents="stroke"
                  onMouseEnter={() => setActiveRegion(region)}
                  onMouseLeave={() => setActiveRegion(null)}
                  onFocus={() => setActiveRegion(region)}
                  onBlur={() => setActiveRegion(null)}
                  tabIndex={0}
                  aria-label={`${region} p95 latency trend`}
                />
                <path
                  d={path}
                  stroke={REGION_COLORS[region] ?? '#9aa3b8'}
                  strokeWidth={activeRegion === region ? 4 : 2.2}
                  opacity={isActive ? 1 : 0.22}
                  pointerEvents="none"
                />
              </g>
            ) : null;
          })}
        </g>
        <g>
          {Array.from(seriesByRegion.entries()).flatMap(([region, points]) =>
            points.map((point) => (
              <circle
                key={`${region}-${point.ts}`}
                cx={xForTimestamp(point.ts)}
                cy={yFor(point.p95_ms)}
                r={activeRegion === region ? '3.75' : '2.6'}
                fill={REGION_COLORS[region] ?? '#9aa3b8'}
                opacity={activeRegion === null || activeRegion === region ? 1 : 0.2}
              />
            )),
          )}
        </g>
      </svg>
    </ChartFrame>
  );
}
